import React, { useState, useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { GlobalFonts } from '../../styles/GlobalFonts';
import { initTelegramWebApp } from '../../utils/telegramInit';
import { cleanupExpiredFiles } from '../../utils/storage';

// Импорт компонентов экранов
import FirstScreen from '../FirstLine/FirstScreen';

// Первая ветка СВОП
import SecondScreen from '../SecondLine/SecondScreen';
import SecondTheory1 from '../SecondLine/SecondTheory1';
import SecondTheory2 from '../SecondLine/SecondTheory2';
import SecondTheory3 from '../SecondLine/SecondTheory3';
import SecondTheory4 from '../SecondLine/SecondTheory4';
import SecondTheory5 from '../SecondLine/SecondTheory5';
import SecondPractica1 from '../SecondLine/SecondPractica1';

// Вторая ветка ЛИКВИДНОСТЬ
import ThreeScreen from '../ThreeLine/ThreeScreen';
import ThreeTheory1 from '../ThreeLine/ThreeTheory1';
import ThreeTheory2 from '../ThreeLine/ThreeTheory2';
import ThreeTheory3 from '../ThreeLine/ThreeTheory3';
import ThreeTheory4 from '../ThreeLine/ThreeTheory4';
import ThreeTheory5 from '../ThreeLine/ThreeTheory5';
import ThreePractica1 from '../ThreeLine/ThreePractica1';
import ThreePractica2 from '../ThreeLine/ThreePractica2';
import ThreePractica3 from '../ThreeLine/ThreePractica3';
import ThreePractica4 from '../ThreeLine/ThreePractica4';

// Третья ветка ФАРМИНГ
import FourScreen from '../FourLine/FourScreen';
import FourTheory1 from '../FourLine/FourTheory1';
import FourTheory2 from '../FourLine/FourTheory2';
import FourTheory3 from '../FourLine/FourTheory3';
import FourTheory4 from '../FourLine/FourTheory4';
import FourTheory5 from '../FourLine/FourTheory5';
import FourTheory6 from '../FourLine/FourTheory6';
import FourPractica1 from '../FourLine/FourPractica1';
import FourPractica2 from '../FourLine/FourPractica2';

// Пятая ветка
import FiveScreen from '../FiveLine/FiveScreen';
import FiveTheory1 from '../FiveLine/FiveTheory1';
import FiveTheory2 from '../FiveLine/FiveTheory2';
import FiveTheory3 from '../FiveLine/FiveTheory3';
import FivePractica1 from '../FiveLine/FivePractica1';
import FivePractica2 from '../FiveLine/FivePractica2';

import './App.css';

// Простая анимация затемнения/появления
const fadeVariants = {
  initial: {
    opacity: 0,
  },
  animate: {
    opacity: 1,
    transition: {
      duration: 0.3,
      ease: 'easeInOut',
    },
  },
  exit: {
    opacity: 0,
    transition: {
      duration: 0.3,
      ease: 'easeInOut',
    },
  },
};

// Основные стили контейнера
const containerStyles = {
  height: '100vh',
  overflow: 'hidden',
  background: '#000000',
  position: 'fixed' as const,
  width: '100%',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  msOverflowStyle: 'none' as const,
  scrollbarWidth: 'none' as const,
  WebkitOverflowScrolling: 'touch' as const,
  touchAction: 'none' as const,
  userSelect: 'none' as const,
};

const STORAGE_KEY = 'tgreact-vite-progress';

const getInitialProgress = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      return JSON.parse(saved);
    }
  } catch (e) {
    console.log(e);
  }
  return null;
};

const App = () => {
  // --- 1. Инициализация состояния с localStorage ---
  const initial = getInitialProgress();

  const [screenIndex, setScreenIndex] = useState(initial?.screenIndex ?? 0);
  const [subScreenIndex, setSubScreenIndex] = useState(
    initial?.subScreenIndex ?? 0
  );
  const [theoryIndex, setTheoryIndex] = useState(initial?.theoryIndex ?? 1);
  const [threeSubScreenIndex, setThreeSubScreenIndex] = useState(
    initial?.threeSubScreenIndex ?? 0
  );
  const [threeTheoryIndex, setThreeTheoryIndex] = useState(
    initial?.threeTheoryIndex ?? 1
  );
  const [threePracticaIndex, setThreePracticaIndex] = useState(
    initial?.threePracticaIndex ?? 0
  );
  const [threePracticaStage, setThreePracticaStage] = useState(
    initial?.threePracticaStage ?? 1
  );
  const [fourSubScreenIndex, setFourSubScreenIndex] = useState(
    initial?.fourSubScreenIndex ?? 0
  );
  const [fourTheoryIndex, setFourTheoryIndex] = useState(
    initial?.fourTheoryIndex ?? 1
  );
  const [fourPracticaIndex, setFourPracticaIndex] = useState(
    initial?.fourPracticaIndex ?? 0
  );
  const [fourPracticaStage, setFourPracticaStage] = useState(
    initial?.fourPracticaStage ?? 1
  );
  const [fiveSubScreenIndex, setFiveSubScreenIndex] = useState(
    initial?.fiveSubScreenIndex ?? 0
  );
  const [fiveTheoryIndex, setFiveTheoryIndex] = useState(
    initial?.fiveTheoryIndex ?? 1
  );
  const [fivePracticaIndex, setFivePracticaIndex] = useState(
    initial?.fivePracticaIndex ?? 0
  );
  const [fivePracticaStage, setFivePracticaStage] = useState(
    initial?.fivePracticaStage ?? 1
  );

  // Инициализация Telegram Web App при загрузке приложения
  useEffect(() => {
    initTelegramWebApp();

    // Проверяем просроченные файлы при загрузке приложения
    cleanupExpiredFiles();

    // Проверяем каждые 24 часа
    const interval = setInterval(cleanupExpiredFiles, 24 * 60 * 60 * 1000);

    return () => clearInterval(interval);
  }, []);

  // --- 2. Сохранять прогресс при изменении любого индекса ---
  useEffect(() => {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        screenIndex,
        subScreenIndex,
        theoryIndex,
        threeSubScreenIndex,
        threeTheoryIndex,
        threePracticaStage,
        fourSubScreenIndex,
        fourTheoryIndex,
        fourPracticaStage,
        fiveSubScreenIndex,
        fiveTheoryIndex,
        fivePracticaStage,
      })
    );
  }, [
    screenIndex,
    subScreenIndex,
    theoryIndex,
    threeSubScreenIndex,
    threeTheoryIndex,
    threePracticaStage,
    fourSubScreenIndex,
    fourTheoryIndex,
    fourPracticaStage,
    fiveSubScreenIndex,
    fiveTheoryIndex,
    fivePracticaStage,
  ]);

  // Добавим функции для сброса состояний
  const resetTheoryStates = () => {
    setTheoryIndex(1);
    setThreeTheoryIndex(1);
    setFourTheoryIndex(1);
    setFiveTheoryIndex(1);
  };

  const resetPracticaStates = () => {
    setThreePracticaStage(1);
    setFourPracticaStage(1);
    setFivePracticaStage(1);
  };

  const resetSubScreenStates = () => {
    setSubScreenIndex(0);
    setThreeSubScreenIndex(0);
    setFourSubScreenIndex(0);
    setFiveSubScreenIndex(0);
  };

  // Обновим useEffect для отслеживания изменения screenIndex
  useEffect(() => {
    // При переходе на новый экран сбрасываем все состояния
    resetTheoryStates();
    resetPracticaStates();
    resetSubScreenStates();
  }, [screenIndex]);

  // Добавим функцию для сброса всех состояний
  const resetAllStates = () => {
    setScreenIndex(0);
    resetSubScreenStates();
    resetTheoryStates();
    resetPracticaStates();
  };

  // Добавим useEffect для отслеживания изменения screenIndex
  useEffect(() => {
    // При переходе на новый экран сбрасываем состояния предыдущих экранов
    if (screenIndex === 1) {
      setThreeSubScreenIndex(0);
      setFourSubScreenIndex(0);
      setFiveSubScreenIndex(0);
    } else if (screenIndex === 2) {
      setSubScreenIndex(0);
      setFourSubScreenIndex(0);
      setFiveSubScreenIndex(0);
    } else if (screenIndex === 3) {
      setSubScreenIndex(0);
      setThreeSubScreenIndex(0);
      setFiveSubScreenIndex(0);
    } else if (screenIndex === 4) {
      setSubScreenIndex(0);
      setThreeSubScreenIndex(0);
      setFourSubScreenIndex(0);
    }
  }, [screenIndex]);

  // Добавим useEffect для отслеживания изменений subScreenIndex
  useEffect(() => {
    if (subScreenIndex === 0) {
      setTheoryIndex(1); // Сбрасываем индекс теории при возврате на главную
    }
  }, [subScreenIndex]);

  return (
    <>
      <GlobalFonts />
      <div style={containerStyles}>
        <AnimatePresence mode="wait">
          {/* Первый экран */}
          {screenIndex === 0 && (
            <motion.div
              key="first"
              variants={fadeVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              style={{ ...containerStyles }}
            >
              <FirstScreen onSwipeLeft={() => setScreenIndex(1)} />
            </motion.div>
          )}

          {/* Второй экран */}
          {screenIndex === 1 && subScreenIndex === 0 && (
            <motion.div
              key="second"
              variants={fadeVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              style={{ ...containerStyles }}
            >
              <SecondScreen
                onSwipeLeft={() => setSubScreenIndex(2)}
                toHome={resetAllStates}
                onSwipeRight={() => setScreenIndex(0)}
              />
            </motion.div>
          )}

          {/* Second Screen ТЕОРИЯ */}
          {screenIndex === 1 && subScreenIndex === 2 && (
            <div style={{ ...containerStyles, position: 'relative' }}>
              {[1, 2, 3, 4, 5].map((idx) => (
                <AnimatePresence key={idx} mode="wait">
                  {theoryIndex === idx && (
                    <motion.div
                      key={idx}
                      variants={fadeVariants}
                      initial="initial"
                      animate="animate"
                      exit="exit"
                      style={{
                        position: 'absolute',
                        inset: 0,
                        pointerEvents: theoryIndex === idx ? 'auto' : 'none',
                        zIndex: theoryIndex === idx ? 2 : 1,
                      }}
                    >
                      {idx === 1 && (
                        <SecondTheory1
                          onSwipeLeft={() => setTheoryIndex(2)}
                          onSwipeRight={() => {
                            setSubScreenIndex(0);
                          }}
                          toHome={resetAllStates}
                        />
                      )}
                      {idx === 2 && (
                        <SecondTheory2
                          onSwipeLeft={() => setTheoryIndex(3)}
                          onSwipeRight={() => setTheoryIndex(1)}
                          toHome={resetAllStates}
                        />
                      )}
                      {idx === 3 && (
                        <SecondTheory3
                          onSwipeLeft={() => setTheoryIndex(4)}
                          onSwipeRight={() => setTheoryIndex(2)}
                          toHome={resetAllStates}
                        />
                      )}
                      {idx === 4 && (
                        <SecondTheory4
                          onSwipeLeft={() => setTheoryIndex(5)}
                          onSwipeRight={() => setTheoryIndex(3)}
                          toHome={resetAllStates}
                        />
                      )}
                      {idx === 5 && (
                        <SecondTheory5
                          onSwipeLeft={() => {
                            setSubScreenIndex(1);
                            setTheoryIndex(1);
                          }}
                          onSwipeRight={() => {
                            setTheoryIndex(4);
                          }}
                          toHome={resetAllStates}
                        />
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              ))}
            </div>
          )}

          {/* Second Screen ПРАКТИКА */}
          {screenIndex === 1 && subScreenIndex === 1 && (
            <motion.div
              key="secondPractica"
              variants={fadeVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              style={{ ...containerStyles }}
            >
              <SecondPractica1
                onSwipeRight={() => {
                  setTheoryIndex(5);
                  setSubScreenIndex(2);
                  resetPracticaStates();
                }}
                onNavigateToThreeScreen={() => {
                  setScreenIndex(2);
                  setSubScreenIndex(0);
                  setTheoryIndex(1); // Сбрасываем индекс теории при переходе к следующему разделу
                }}
                toHome={resetAllStates}
              />
            </motion.div>
          )}

          {/* ThreeScreen ГЛАВНАЯ */}
          {screenIndex === 2 && threeSubScreenIndex === 0 && (
            <motion.div
              key="three"
              variants={fadeVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              style={{ ...containerStyles }}
            >
              <ThreeScreen
                onSwipeLeft={() => {
                  setThreeSubScreenIndex(2);
                  setThreeTheoryIndex(1);
                }}
                toHome={resetAllStates}
                onSwipeRight={() => {
                  setThreeSubScreenIndex(0);
                  setThreeTheoryIndex(1);
                  setScreenIndex(1);
                }}
              />
            </motion.div>
          )}

          {/* Three Screen ТЕОРИЯ */}
          {screenIndex === 2 && threeSubScreenIndex === 2 && (
            <div style={{ ...containerStyles, position: 'relative' }}>
              {[1, 2, 3, 4, 5].map((idx) => (
                <AnimatePresence key={idx} mode="wait">
                  {threeTheoryIndex === idx && (
                    <motion.div
                      key={idx}
                      variants={fadeVariants}
                      initial="initial"
                      animate="animate"
                      exit="exit"
                      style={{
                        position: 'absolute',
                        inset: 0,
                        pointerEvents:
                          threeTheoryIndex === idx ? 'auto' : 'none',
                        zIndex: threeTheoryIndex === idx ? 2 : 1,
                      }}
                    >
                      {idx === 1 && (
                        <ThreeTheory1
                          onSwipeLeft={() => setThreeTheoryIndex(2)}
                          onSwipeRight={() => setThreeSubScreenIndex(0)}
                          toHome={resetAllStates}
                        />
                      )}
                      {idx === 2 && (
                        <ThreeTheory2
                          onSwipeLeft={() => setThreeTheoryIndex(3)}
                          onSwipeRight={() => setThreeTheoryIndex(1)}
                          toHome={resetAllStates}
                        />
                      )}
                      {idx === 3 && (
                        <ThreeTheory3
                          onSwipeLeft={() => setThreeTheoryIndex(4)}
                          onSwipeRight={() => setThreeTheoryIndex(2)}
                          toHome={resetAllStates}
                        />
                      )}
                      {idx === 4 && (
                        <ThreeTheory4
                          onSwipeLeft={() => setThreeTheoryIndex(5)}
                          onSwipeRight={() => setThreeTheoryIndex(3)}
                          toHome={resetAllStates}
                        />
                      )}
                      {idx === 5 && (
                        <ThreeTheory5
                          onSwipeLeft={() => {
                            setThreeSubScreenIndex(1); // Переходим к практике
                            setThreeTheoryIndex(1); // Сбрасываем индекс теории
                          }}
                          onSwipeRight={() => {
                            setThreeTheoryIndex(4);
                          }}
                          toHome={resetAllStates}
                        />
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              ))}
            </div>
          )}

          {/* Three Screen ПРАКТИКА */}
          {screenIndex === 2 && threeSubScreenIndex === 1 && (
            <motion.div
              key="threePractica"
              variants={fadeVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              style={{ ...containerStyles }}
            >
              {threePracticaStage === 1 && (
                <ThreePractica1
                  onSwipeRight={() => {
                    setThreeSubScreenIndex(2); // Возвращаемся к теории
                    setThreeTheoryIndex(5);
                  }}
                  onNavigateToPractica2={() => {
                    setThreePracticaStage(2); // Устанавливаем следующий этап практики
                  }}
                  onNavigateToTheory={() => {
                    setThreeSubScreenIndex(2); // Переключаемся в режим теории
                    setThreeTheoryIndex(4); // Устанавливаем нужный слайд теории
                  }}
                  toHome={resetAllStates}
                />
              )}
              {threePracticaStage === 2 && (
                <ThreePractica2
                  onSwipeRight={() => setThreePracticaStage(1)}
                  onNavigateToPractica3={() => setThreePracticaStage(3)}
                  toHome={resetAllStates}
                />
              )}
              {threePracticaStage === 3 && (
                <ThreePractica3
                  onSwipeLeft={() => setThreePracticaStage(4)}
                  onSwipeRight={() => setThreePracticaStage(2)}
                  toHome={resetAllStates}
                />
              )}
              {threePracticaStage === 4 && (
                <ThreePractica4
                  onSwipeLeft={() => {
                    setScreenIndex(3); // Устанавливаем индекс 3 для перехода на FourScreen
                    setThreePracticaStage(1);
                    setFourSubScreenIndex(0);
                  }}
                  onSwipeRight={() => setThreePracticaStage(3)}
                  toHome={resetAllStates}
                  handleDown={() => setScreenIndex(3)}
                />
              )}
            </motion.div>
          )}

          {/* FourScreen ГЛАВНАЯ */}
          {screenIndex === 3 && fourSubScreenIndex === 0 && (
            <motion.div
              key="four"
              variants={fadeVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              style={{ ...containerStyles }}
            >
              <FourScreen
                onSwipeLeft={() => {
                  setFourSubScreenIndex(2);
                  setFourTheoryIndex(1);
                }}
                toHome={resetAllStates}
                onSwipeRight={() => {
                  setFourSubScreenIndex(0);
                  setFourTheoryIndex(1);
                  setScreenIndex(2);
                }}
              />
            </motion.div>
          )}

          {/* Four Screen ТЕОРИЯ */}
          {screenIndex === 3 && fourSubScreenIndex === 2 && (
            <div style={{ ...containerStyles, position: 'relative' }}>
              {[1, 2, 3, 4, 5, 6].map((idx) => (
                <AnimatePresence key={idx} mode="wait">
                  {fourTheoryIndex === idx && (
                    <motion.div
                      key={idx}
                      variants={fadeVariants}
                      initial="initial"
                      animate="animate"
                      exit="exit"
                      style={{
                        position: 'absolute',
                        inset: 0,
                        pointerEvents:
                          fourTheoryIndex === idx ? 'auto' : 'none',
                        zIndex: fourTheoryIndex === idx ? 2 : 1,
                      }}
                    >
                      {idx === 1 && (
                        <FourTheory1
                          onSwipeLeft={() => setFourTheoryIndex(2)}
                          onSwipeRight={() => setFourSubScreenIndex(0)}
                          toHome={resetAllStates}
                        />
                      )}
                      {idx === 2 && (
                        <FourTheory2
                          onSwipeLeft={() => setFourTheoryIndex(3)}
                          onSwipeRight={() => setFourTheoryIndex(1)}
                          toHome={resetAllStates}
                        />
                      )}
                      {idx === 3 && (
                        <FourTheory3
                          onSwipeLeft={() => setFourTheoryIndex(4)}
                          onSwipeRight={() => setFourTheoryIndex(2)}
                          toHome={resetAllStates}
                        />
                      )}
                      {idx === 4 && (
                        <FourTheory4
                          onSwipeLeft={() => setFourTheoryIndex(5)}
                          onSwipeRight={() => setFourTheoryIndex(3)}
                          toHome={resetAllStates}
                        />
                      )}
                      {idx === 5 && (
                        <FourTheory5
                          onSwipeLeft={() => setFourTheoryIndex(6)}
                          onSwipeRight={() => setFourTheoryIndex(4)}
                          toHome={resetAllStates}
                        />
                      )}
                      {idx === 6 && (
                        <FourTheory6
                          onSwipeLeft={() => {
                            setFourSubScreenIndex(1); // Переходим к практике
                            setFourTheoryIndex(1);
                          }}
                          onSwipeRight={() => {
                            setFourTheoryIndex(5);
                          }}
                          toHome={resetAllStates}
                        />
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              ))}
            </div>
          )}

          {/* Four Screen ПРАКТИКА */}
          {screenIndex === 3 && fourSubScreenIndex === 1 && (
            <motion.div
              key="fourPractica"
              variants={fadeVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              style={{ ...containerStyles }}
            >
              {fourPracticaStage === 1 && (
                <FourPractica1
                  onSwipeLeft={() => setFourTheoryIndex(6)}
                  onSwipeRight={() => {
                    setFourSubScreenIndex(2); // Возвращаемся к теории
                    setFourTheoryIndex(6);
                  }}
                  onNavigateToPractica2={() => setFourPracticaStage(2)}
                  onNavigateToTheory={() => {
                    setFourSubScreenIndex(2);
                    setFourTheoryIndex(1);
                  }}
                  toHome={resetAllStates}
                />
              )}
              {fourPracticaStage === 2 && (
                <FourPractica2
                  onSwipeLeft={() => {
                    setScreenIndex(4); // переход на FiveScreen первым!
                    setFourPracticaStage(1);
                    setFourSubScreenIndex(0);
                    setFiveSubScreenIndex(0);
                  }}
                  onSwipeRight={() => setFourPracticaStage(1)}
                  toHome={resetAllStates}
                />
              )}
            </motion.div>
          )}

          {/* FiveScreen ГЛАВНАЯ */}
          {screenIndex === 4 && fiveSubScreenIndex === 0 && (
            <motion.div
              key="five"
              variants={fadeVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              style={{ ...containerStyles }}
            >
              <FiveScreen
                onSwipeLeft={() => {
                  setFiveSubScreenIndex(2);
                  setFiveTheoryIndex(1);
                }}
                toHome={resetAllStates}
                onSwipeRight={() => {
                  setFiveSubScreenIndex(0);
                  setFiveTheoryIndex(1);
                  setScreenIndex(3);
                }}
              />
            </motion.div>
          )}

          {/* FiveScreen ТЕОРИЯ */}
          {screenIndex === 4 && fiveSubScreenIndex === 2 && (
            <div style={{ ...containerStyles, position: 'relative' }}>
              {[1, 2, 3].map((idx) => (
                <AnimatePresence key={idx} mode="wait">
                  {fiveTheoryIndex === idx && (
                    <motion.div
                      key={idx}
                      variants={fadeVariants}
                      initial="initial"
                      animate="animate"
                      exit="exit"
                      style={{
                        position: 'absolute',
                        inset: 0,
                        pointerEvents:
                          fiveTheoryIndex === idx ? 'auto' : 'none',
                        zIndex: fiveTheoryIndex === idx ? 2 : 1,
                      }}
                    >
                      {idx === 1 && (
                        <FiveTheory1
                          onSwipeRight={() => {
                            setFiveSubScreenIndex(0);
                            setFiveTheoryIndex(1);
                            setFivePracticaIndex(0);
                            setFivePracticaStage(1);
                          }}
                          onSwipeLeft={() => setFiveTheoryIndex(2)}
                          toHome={resetAllStates}
                        />
                      )}
                      {idx === 2 && (
                        <FiveTheory2
                          onSwipeRight={() => setFiveTheoryIndex(1)}
                          onSwipeLeft={() => setFiveTheoryIndex(3)}
                          toHome={resetAllStates}
                        />
                      )}
                      {idx === 3 && (
                        <FiveTheory3
                          onSwipeRight={() => {
                            setFiveTheoryIndex(2);
                          }}
                          onSwipeLeft={() => {
                            setFiveSubScreenIndex(1); // Переходим к практике
                            setFivePracticaStage(1);
                          }}
                          toHome={resetAllStates}
                        />
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              ))}
            </div>
          )}

          {/* FiveScreen ПРАКТИКА */}
          {screenIndex === 4 && fiveSubScreenIndex === 1 && (
            <motion.div
              key="fivePractica"
              variants={fadeVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              style={{ ...containerStyles }}
            >
              {fivePracticaStage === 1 && (
                <FivePractica1
                  onSwipeLeft={() => setFiveTheoryIndex(1)}
                  onSwipeRight={() => {
                    setFiveSubScreenIndex(2);
                    setFiveTheoryIndex(3);
                  }}
                  onNavigateToPractica2={() => setFivePracticaStage(2)}
                  toHome={resetAllStates}
                />
              )}
              {fivePracticaStage === 2 && (
                <FivePractica2
                  onRestart={() => {
                    resetAllStates(); // Сбрасываем все состояния
                  }}
                  onSwipeRight={() => setFivePracticaStage(1)}
                  toHome={resetAllStates}
                />
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </>
  );
};

export default App;
